import React, { useState, useEffect } from "react";
import api from "../api/axios";
import VoucherDetails from "../components/modal/VoucherDetails";

function LedgerStatement() {
  // Calculate current month dates
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 2);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const formatDate = (d) => d.toISOString().split("T")[0];

  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [filters, setFilters] = useState({
    startDate: formatDate(firstDay),
    endDate: formatDate(lastDay),
    accountId: "",
  });

  const token = localStorage.getItem("accessToken");

  const branch_id = sessionStorage.getItem("selectedContext")
    ? JSON.parse(sessionStorage.getItem("selectedContext")).branch
    : 0;

  // Fetch ledger data
  const fetchLedgerData = async (pageNumber = 1) => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: pageNumber,
        branch_id: branch_id, // 🔥 MANDATORY
        ...(filters.startDate && { start_date: filters.startDate }),
        ...(filters.endDate && { end_date: filters.endDate }),
        ...(filters.accountId && { account_id: filters.accountId }),
      });

      const res = await api.get(`/ledger-statement?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Fetched ledger data:", res.data);

      setLedgerData(res.data.data);
      setPage(res.data.current_page);
      setLastPage(res.data.last_page);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load current month automatically
  useEffect(() => {
    fetchLedgerData();
  }, []);

  // Handle filter inputs
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply filter
  const handleApplyFilters = () => {
    setPage(1);
    fetchLedgerData(1);
  };

  // Reset to current month
  const handleResetFilters = () => {
    setFilters({
      startDate: formatDate(firstDay),
      endDate: formatDate(lastDay),
      accountId: "",
    });

    setPage(1);
    fetchLedgerData(1);
  };
  // Group ledger data before rendering
  const groupedLedgerData = Object.values(
    ledgerData.reduce((acc, item) => {
      if (!acc[item.ledger_id]) {
        acc[item.ledger_id] = {
          ledger_id: item.ledger_id,
          ledger_code: item.ledger_code || "-", // Adjust if needed
          ledger_name: item.ledger_name || "-", // Adjust if needed
          opening_balance: item.opening_balance || 0,
          entries: [],
        };
      }

      acc[item.ledger_id].entries.push({
        voucher_id: item.voucher_id,
        voucher_date: item.voucher_date,
        reference: item.series_no,
        voucher_type: item.voucher_type,
        narration: item.narration,
        description: item.description,
        debit: item.debit,
        credit: item.credit,
      });

      return acc;
    }, {})
  );

  return (
    <>
      {/* Page Title */}
      <div className="row mb-3">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Ledger Statement</h4>
          <ol className="breadcrumb m-0">
            <li className="breadcrumb-item">
              <a href="#">Accounts</a>
            </li>
            <li className="breadcrumb-item active">Ledger Statement</li>
          </ol>
        </div>
      </div>

      {/* Filter Section */}
      <div className="row mb-3">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Filters</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {/* From Date */}
                <div className="col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>

                {/* To Date */}
                <div className="col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>

                {/* Account ID */}
                <div className="col-md-3">
                  <label className="form-label">Account ID</label>
                  <input
                    type="text"
                    className="form-control"
                    name="accountId"
                    value={filters.accountId}
                    onChange={handleFilterChange}
                    placeholder="Enter Account ID"
                  />
                </div>

                {/* Buttons */}
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary me-2"
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleResetFilters}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                Ledger Statement ({new Date(filters.startDate).toLocaleDateString("en-GB")} To {new Date(filters.endDate).toLocaleDateString("en-GB")})
              </h5>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border"></div>
                  <p>Loading...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered ledger-table">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Account</th>
                        <th>Date</th>
                        <th>Reference</th>
                        <th>Description</th>
                        <th className="text-end">Debit</th>
                        <th className="text-end">Credit</th>
                        <th className="text-end">Balance</th>
                      </tr>
                    </thead>

                    <tbody>
                      {groupedLedgerData.length > 0 ? (
                        groupedLedgerData.map((ledger, idx) => {
                          let runningBal = parseFloat(
                            ledger.opening_balance || 0
                          );

                          return (
                            <React.Fragment key={idx}>
                              {/* Ledger Header */}
                              <tr className="table-secondary">
                                <td colSpan="7" className="fw-bold">
                                  <div className="d-flex flex-column">
                                    <span className="fs-6">
                                      {ledger.ledger_code}
                                    </span>
                                    <span className="fs-5">
                                      {ledger.ledger_name}
                                    </span>
                                  </div>
                                </td>
                              </tr>

                              {/* Beginning Balance */}
                              <tr>
                                <td></td>
                                <td>{new Date(filters.startDate).toLocaleDateString("en-GB")}</td>
                                <td></td>
                                <td colSpan="3">
                                  Beginning Balance
                                </td>
                                <td className="text-end fw-bold">
                                  {runningBal.toFixed(2)}
                                </td>
                              </tr>

                              {/* Transaction Rows */}
                              {ledger.entries.length > 0 ? (
                                ledger.entries.map((row, tIndex) => {
                                  runningBal +=
                                    parseFloat(row.debit || 0) -
                                    parseFloat(row.credit || 0);

                                  return (
                                    <tr key={tIndex}>
                                      <td></td>
                                      <td>{new Date(row.voucher_date).toLocaleDateString("en-GB")}</td>
                                      <td className="fw-bold">
                                        <span
                                          className="voucher-link"
                                          onClick={() =>
                                            window.openPanel(
                                              "Journal Entry Details",
                                              <VoucherDetails
                                                voucherId={row.voucher_id}
                                                startDate={filters.startDate}
                                                endDate={filters.endDate}
                                              />
                                            )
                                          }
                                        >
                                          {row.reference || "-"}
                                        </span>
                                      </td>
                                      <td>{row.narration}</td>

                                      <td className="text-end">
                                        {row.debit !== "0.00" ? row.debit : "-"}
                                      </td>

                                      <td className="text-end">
                                        {row.credit !== "0.00"
                                          ? row.credit
                                          : "-"}
                                      </td>

                                      <td className="text-end fw-bold">
                                        {runningBal.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td
                                    colSpan="7"
                                    className="text-center text-muted"
                                  >
                                    No transactions found
                                  </td>
                                </tr>
                              )}

                              {/* Ending Balance */}
                              <tr className=" fw-bold">
                                <td></td>
                                <td>{new Date(filters.endDate).toLocaleDateString("en-GB")}</td>
                                <td></td>
                                <td colSpan="3">Ending Balance</td>
                                <td className="text-end">
                                  {runningBal.toFixed(2)}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No data found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {ledgerData.length > 0 && (
                <div className="d-flex justify-content-between mt-3">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => fetchLedgerData(page - 1)}
                    disabled={page <= 1}
                  >
                    Previous
                  </button>

                  <span>
                    Page {page} of {lastPage}
                  </span>

                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => fetchLedgerData(page + 1)}
                    disabled={page >= lastPage}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      {/* {ledgerData.length > 0 && (
        <div className="row mt-3">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Summary</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6>Total Debit</h6>
                        <h4 className="text-danger">
                          {ledgerData
                            .reduce(
                              (sum, item) =>
                                sum + (parseFloat(item.debit) || 0),
                              0
                            )
                            .toFixed(2)}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6>Total Credit</h6>
                        <h4 className="text-success">
                          {ledgerData
                            .reduce(
                              (sum, item) =>
                                sum + (parseFloat(item.credit) || 0),
                              0
                            )
                            .toFixed(2)}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6>Total Accounts</h6>
                        <h4 className="text-primary">
                          {
                            new Set(
                              ledgerData.map((i) => i.accountId || i.account_id)
                            ).size
                          }
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
}

export default LedgerStatement;
